// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
// Contexts
import { CaseContext, AuthContext } from "Stores";
import { WebsocketContext } from "Providers";
// Types
import { TCase, TIdAndName, TNotificationMessageDTO, TTechnology } from "Types";
// styles
import styles from "./conceptTechnologyHeader.module.scss";
// Components
import { FindestEditableSpan } from "Components";
import { TechnologyListItemVotes } from "../ConceptTechnologyList/TechnologyListItem/TechnologyListItemVotes";
import { TechnologyListItemRates } from "../ConceptTechnologyList/TechnologyListItem/TechnologyListItemRates";
// Enums
import { CaseActionTypeEnum, ToastTypeEnum } from "Enums";
// Interfaces
import { ISetCaseConceptTechnologyListAction } from "Interfaces";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Constants
import { CasePhaseConstants, FeatureToggleConstants, HubConstants } from "Constants";
// Custom hooks
import { useDebounce } from "Hooks";
// Helpers
import { ToastHelperSingleton } from "Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { SearchableDropdown } from "Components/Shared/SearchableDropdown";

export const ConceptTechnologyHeader: FC = () => {
    // State
    const [currentTechnology, setCurrentTechnology] = useState<TTechnology | undefined>(undefined);
    const [currentTechnologyEditedName, setCurrentTechnologyEditedName] = useState<string | undefined>(undefined);
    const [isCopyCaseSearchShown, setIsCopyCaseSearchShown] = useState(false);

    // Contexts
    const { authState } = useContext(AuthContext);
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { webSocketController } = useContext(WebsocketContext);
    
    // Let
    const debouncedEditedName = useDebounce(currentTechnologyEditedName, 500);
    const technologyNameResourceName = caseState.selectedTechnologyId ? `${HubConstants.TECHNOLOGY_NAME_RESOURCE_NAME_PREFIX}${caseState.selectedTechnologyId}` : "";

    // Logic
    useEffect(() => {
        if (!caseState.conceptTechnologyList || !caseState.selectedTechnologyId) return;

        for (const concept of caseState.conceptTechnologyList.concepts) {
            for (const technology of concept.technologies) {
                if (technology.id === caseState.selectedTechnologyId) {
                    setCurrentTechnologyEditedName(undefined);
                    setCurrentTechnology(technology);
                    return;
                }
            }
        }

        if (caseState.conceptTechnologyList.additionalTechnologies) {
            for (const userAddedTechnology of caseState.conceptTechnologyList.additionalTechnologies) {
                if (userAddedTechnology.id === caseState.selectedTechnologyId) {
                    setCurrentTechnologyEditedName(undefined);
                    setCurrentTechnology(userAddedTechnology);
                    return;
                }
            }
        }

        return;
    }, [caseState.conceptTechnologyList, caseState.selectedTechnologyId]);

    const handleTechnologyNameUpdated = useCallback((technologyNameUpdated: string) => {
        // safety-checks
        if (!caseState.conceptTechnologyList || !caseState.selectedTechnologyId || !currentTechnology) return;
        
        // update current technology name
        currentTechnology.name = technologyNameUpdated;
        setCurrentTechnology(currentTechnology);

        // update technology name in case state on concept technology list
        for (const concept of caseState.conceptTechnologyList.concepts) {
            for (const technology of concept.technologies) {
                if (technology.id === currentTechnology.id) {
                    technology.name = technologyNameUpdated;
                }
            }
        }
        for (const userAddedTechnology of caseState.conceptTechnologyList.additionalTechnologies) {
            if (userAddedTechnology.id === currentTechnology.id) {
                userAddedTechnology.name = technologyNameUpdated;
            }
        }

        caseDispatch({ type: CaseActionTypeEnum.SetCaseConceptTechnologyList, caseConceptTechnologyList: caseState.conceptTechnologyList } as ISetCaseConceptTechnologyListAction);
    }, [caseDispatch, caseState.conceptTechnologyList, caseState.selectedTechnologyId, currentTechnology]);

    const updateTechnologyName = useCallback(async (name: string) => {
        // safety-checks
        if (!caseState.conceptTechnologyList || !caseState.selectedTechnologyId || !currentTechnology || !name || !currentTechnologyEditedName) return;
        setCurrentTechnologyEditedName(undefined);
        
        const isSuccess = await TechnologyControllerSingleton.updateTechnology(currentTechnology.id, true, name, undefined);
        
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to change the technology name.");
            return;
        }

    }, [caseState.conceptTechnologyList, caseState.selectedTechnologyId, currentTechnology, currentTechnologyEditedName]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new technology
        // or when displaying a new technology
        if (!currentTechnology ||
            !caseState.selectedTechnologyId ||
            currentTechnology.id !== caseState.selectedTechnologyId ||
            !debouncedEditedName) return;
        // send new name to the server
        updateTechnologyName(debouncedEditedName);
    }, [caseState.selectedTechnologyId, currentTechnology, debouncedEditedName, updateTechnologyName]);

    const handleNotificationMessages = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        ToastHelperSingleton.handleNotificationMessage(notificationMessage, authState.userEmail);
    }, [authState]);

    // manage web sockets related events
    useEffect(() => {
        // safety-check
        if (!technologyNameResourceName) return;
        
        // we add two handlers to listen on update and notification messages related to current technology name resource name
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${technologyNameResourceName}`, 
            handleTechnologyNameUpdated);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyNameResourceName}`, 
            handleNotificationMessages);
        
        // remove handlers on clean up time
        return () => {
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${technologyNameResourceName}`, 
                handleTechnologyNameUpdated);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyNameResourceName}`, 
                handleNotificationMessages);
        };
    }, [handleNotificationMessages, handleTechnologyNameUpdated, technologyNameResourceName, webSocketController]);

    // Handle copy to case click
    const onOpenCaseCopySearchClick = () => {
        setIsCopyCaseSearchShown(!isCopyCaseSearchShown);
    };

    const onCaseToCopyClicked = async (result: unknown): Promise<boolean> => {
        if(!currentTechnology) return false;
        const caseToCopy = result as TCase;

        const isSuccess = await TechnologyControllerSingleton.copyTechnologyAsync(caseToCopy.id, currentTechnology.id);
        if(!isSuccess) return false;

        setIsCopyCaseSearchShown(false);
        ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Technology copied.");
        return true;
    };

    const caseToIdAndName = (result: unknown): TIdAndName => {
        const caseToCopy = result as TCase;

        return {
            id: caseToCopy.id.toString(),
            name: caseToCopy.title
        };
    };

    return (
        <div className={styles.conceptTechnologyHeader}>
            {currentTechnology && currentTechnology.isUserAdded ?
                <h2 className={styles.editableTechnologyTitleContainer}>
                    {caseState.selectedEntityNumbering}
                    <FindestEditableSpan
                        entityId={currentTechnology.id}
                        isEditableTechnologyTitle={true}
                        extraClassName={styles.editableTechnologyTitle}
                        text={currentTechnology.name ? currentTechnology.name : ""}
                        onChangeText={setCurrentTechnologyEditedName} 
                        resourceName={technologyNameResourceName} />
                </h2>
                :
                <h2>{caseState.selectedEntityNumbering} {caseState.selectedEntityName}</h2>
            }
            {currentTechnology ?
                    <>
                        <div
                            className={styles.technologyHeaderVoteContainer}
                            style={{ display: currentTechnology && caseState.casePhase.toLowerCase() === CasePhaseConstants.PHASE_1.toLowerCase() ? "block" : "none" }}>
                            <TechnologyListItemVotes isOutsideSidebar={true} technology={currentTechnology} />
                        </div>
                        <div
                            className={styles.technologyHeaderVoteContainer}
                            style={{ display: currentTechnology && caseState.casePhase.toLowerCase() === CasePhaseConstants.PHASE_2.toLowerCase() ? "block" : "none" }}>
                            <TechnologyListItemRates isOutsideSidebar={true} technology={currentTechnology} />
                        </div>

                        {FeatureToggleConstants.TechnologyCopying ?
                            <>
                                <div onClick={onOpenCaseCopySearchClick}>
                                    <FontAwesomeIcon icon={faCopy} title="Copy to other case" />
                                </div>
                                {isCopyCaseSearchShown ?
                                    <div>
                                        <SearchableDropdown onResultClickAsync={onCaseToCopyClicked} resultToIdAndName={caseToIdAndName}
                                            searchUrl="api/cases?query=" itemName="case" placeholder="Case" doClearValueOnClick={true}
                                            doSearchWhenValueEmpty={false} />
                                    </div>
                                    : null 
                                }
                            </>
                            : null 
                        }
                    </>
                :
                    null
            }
        </div>
    );
};